import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiSetConsents } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const PRIVACY_URL = "https://www.deltapictures.it/privacy-policy/";
export const EMPTY_CONSENTS = { newsletter: null, partners: null };
export const consentsComplete = (c) => c && c.newsletter !== null && c.partners !== null;

const PENDING_KEY = "readoo_pending_consents";
export const stashConsents = (c) => sessionStorage.setItem(PENDING_KEY, JSON.stringify(c));
export const takeStashedConsents = () => {
  try {
    const v = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "null");
    sessionStorage.removeItem(PENDING_KEY);
    return consentsComplete(v) ? v : null;
  } catch { return null; }
};

const ITEMS = [
  { key: "newsletter", text: "all’invio di email da Delta Pictures con newsletter e promozioni su prodotti e servizi propri o di terzi, personalizzate in base ai miei interessi (profilazione), anche tramite la misurazione di aperture e clic sulle email (pixel)." },
  { key: "partners", text: "alla cessione dei dati inseriti nel form ai partner di Delta Pictures, per ricevere da loro newsletter e comunicazioni pubblicitarie sui loro prodotti e servizi." },
];

function Choice({ name, checked, onChange, label, testid }) {
  return (
    <label className="inline-flex items-center gap-1 cursor-pointer">
      <input type="radio" name={name} checked={checked} onChange={onChange} data-testid={testid} className="accent-[hsl(var(--brand,14_70%_50%))] w-3 h-3" />
      <span>{label}</span>
    </label>
  );
}

export function ConsentFields({ value, onChange, error = false, idPrefix = "consent" }) {
  return (
    <div data-testid={`${idPrefix}-fields`} className={`text-[10.5px] leading-snug text-subtle space-y-2 rounded-lg p-2.5 border ${error ? "border-red-500/60" : "border-transparent"}`}>
      <p>
        Letta l’<a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" data-testid={`${idPrefix}-privacy-link`} className="underline hover:text-brand">Informativa Privacy</a>
      </p>
      {ITEMS.map((it) => (
        <div key={it.key}>
          <div className="flex items-center gap-3 font-semibold text-onSurfaceSecondary">
            <Choice name={`${idPrefix}-${it.key}`} checked={value[it.key] === true} onChange={() => onChange({ ...value, [it.key]: true })} label="Acconsento" testid={`${idPrefix}-${it.key}-yes`} />
            <Choice name={`${idPrefix}-${it.key}`} checked={value[it.key] === false} onChange={() => onChange({ ...value, [it.key]: false })} label="Non acconsento" testid={`${idPrefix}-${it.key}-no`} />
          </div>
          <p className="mt-0.5">{it.text}</p>
        </div>
      ))}
    </div>
  );
}

export function ConsentGate() {
  const { user, setUser } = useAuth();
  const [value, setValue] = useState(EMPTY_CONSENTS);
  const [busy, setBusy] = useState(false);
  const open = !!user && user.role === "user" && user.has_consents === false;
  const save = async () => {
    if (!consentsComplete(value)) return toast.error("Seleziona un’opzione per entrambi i consensi");
    setBusy(true);
    try { const r = await apiSetConsents(value); setUser(r.user); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open}>
      <DialogContent data-testid="consent-gate" className="bg-surfaceSecondary border-hair text-onSurface w-[calc(100%-40px)] max-w-md rounded-2xl [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogTitle className="font-serif text-lg font-semibold">Ancora un passaggio</DialogTitle>
        <DialogDescription className="text-xs text-subtle">Indica le tue preferenze sulla privacy per continuare.</DialogDescription>
        <ConsentFields value={value} onChange={setValue} idPrefix="gate-consent" />
        <div>
        <button type="button" onClick={save} disabled={busy} data-testid="consent-gate-submit" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-brand text-onBrand font-semibold text-sm hover:bg-brandSecondary disabled:opacity-60 transition-colors">
          {busy ? <Loader2 size={16} className="animate-spin" /> : "Continua"}
        </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
